import { palette } from '@/modules/defines/styles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import styled from 'styled-components';
import DaekyoHeader from './DaekyoHeader';
import { daekyoEventAPIs } from '@/api';
import { RefreshCw } from 'lucide-react';

type OfferCategory = 1 | 2 | 3;

interface EventDataType {
  id: number;
  title: string;
  description: string;
  category: OfferCategory;
  img: string;
  link_detail: string;
  period: string;
}

const categoryMap: Record<OfferCategory, string> = {
  1: '이벤트',
  2: '다이닝',
  3: '패키지',
};

const categoryColors: Record<OfferCategory, { bg: string; hover: string }> = {
  1: { bg: '#FF6B6B', hover: '#FF5252' }, // 이벤트 - 레드
  2: { bg: '#4ECDC4', hover: '#45B8B0' }, // 다이닝 - 청록색
  3: { bg: '#FFB347', hover: '#FFA733' }, // 패키지 - 오렌지
};

const DaekyoEventContainer = () => {
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<OfferCategory | 'all'>('all');

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['getEventsDaekyo'],
    queryFn: () =>
      daekyoEventAPIs.getEvents({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    staleTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
  });

  const crawlMutation = useMutation({
    mutationFn: () =>
      daekyoEventAPIs.crawlData({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    onSuccess: () => {
      alert('이벤트 데이터 동기화가 완료되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['getEventsDaekyo'] });
    },
    onError: (error: any) => {
      alert(`동기화 중 오류가 발생했습니다: ${error?.message || '알 수 없는 오류'}`);
    },
  });

  const events: EventDataType[] = useMemo(() => {
    return eventsData?.data?.data || [];
  }, [eventsData]);

  const categories = useMemo<OfferCategory[]>(() => {
    const order: OfferCategory[] = [3, 2, 1]; // 패키지, 다이닝, 이벤트
    const present = new Set(events.map((e) => e.category));
    return order.filter((c) => present.has(c));
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (selectedCategory === 'all') return events;
    return events.filter((e) => e.category === selectedCategory);
  }, [events, selectedCategory]);

  const handleCrawl = () => {
    if (window.confirm('마이다스 호텔 홈페이지의 이벤트 데이터를 동기화하시겠습니까?')) {
      crawlMutation.mutate();
    }
  };

  return (
    <Container>
      <DaekyoHeader />
      <Content>
        <SyncSection>
          <SyncButton
            onClick={handleCrawl}
            disabled={crawlMutation.isPending}
            $isLoading={crawlMutation.isPending}
          >
            <RefreshCw size={18} />
            {crawlMutation.isPending ? '동기화 중...' : '이벤트 동기화'}
          </SyncButton>
          <Description>
            해당 버튼을 클릭하여 마이다스 호텔 홈페이지에 있는 이벤트 데이터를 동기화해주세요
          </Description>
        </SyncSection>

        {isLoading ? (
          <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
        ) : !events.length ? (
          <LoadingMessage>등록된 이벤트가 없습니다.</LoadingMessage>
        ) : (
          <>
            {categories.length > 0 && (
              <ChipRow>
                <Chip
                  $selected={selectedCategory === 'all'}
                  onClick={() => setSelectedCategory('all')}
                >
                  전체
                </Chip>
                {categories.map((cat) => (
                  <Chip
                    key={cat}
                    $selected={selectedCategory === cat}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {categoryMap[cat]}
                  </Chip>
                ))}
              </ChipRow>
            )}

            <EventGrid>
              {filteredEvents.map((event) => (
                <EventCard
                  key={event.id}
                  onClick={() => event.link_detail && window.open(event.link_detail, '_blank')}
                  $hasLink={!!event.link_detail}
                >
                  <EventImage src={event.img} alt={event.title} />
                  <EventInfo>
                    <CategoryBadge $category={event.category}>
                      {categoryMap[event.category]}
                    </CategoryBadge>
                    <EventTitle>{event.title}</EventTitle>
                    <EventDescription>{event.description}</EventDescription>
                    {event.period && <EventPeriod>{event.period}</EventPeriod>}
                  </EventInfo>
                </EventCard>
              ))}
            </EventGrid>
          </>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
`;

const SyncSection = styled.div`
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const SyncButton = styled.button<{ $isLoading: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: ${palette.systemBlue};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${({ $isLoading }) => ($isLoading ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  opacity: ${({ $isLoading }) => ($isLoading ? 0.6 : 1)};

  svg {
    animation: ${({ $isLoading }) => ($isLoading ? 'spin 1s linear infinite' : 'none')};
  }

  &:hover {
    background-color: ${({ $isLoading }) => ($isLoading ? palette.systemBlue : '#0066b3')};
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #666;
`;

const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px 0;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const Chip = styled.button<{ $selected: boolean }>`
  font-size: 14px;
  font-weight: 600;
  border-radius: 24px;
  padding: 9px 16px;
  border: none;
  background: ${({ $selected }) => ($selected ? palette.systemBlue : '#e0e0e0')};
  color: ${({ $selected }) => ($selected ? 'white' : '#333')};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ $selected }) => ($selected ? '#0066b3' : '#d0d0d0')};
  }
`;

const EventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }
`;

const EventCard = styled.div<{ $hasLink: boolean }>`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  cursor: ${({ $hasLink }) => ($hasLink ? 'pointer' : 'default')};

  &:hover {
    transform: ${({ $hasLink }) => ($hasLink ? 'translateY(-4px)' : 'none')};
    box-shadow: ${({ $hasLink }) =>
      $hasLink ? '0 4px 16px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  }
`;

const EventImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const EventInfo = styled.div`
  padding: 16px;
`;

const CategoryBadge = styled.span<{ $category: OfferCategory }>`
  display: inline-block;
  padding: 4px 12px;
  background-color: ${({ $category }) => categoryColors[$category].bg};
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  transition: background-color 0.2s;
`;

const EventTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin: 8px 0;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const EventDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin: 8px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const EventPeriod = styled.p`
  font-size: 13px;
  color: #999;
  margin-top: 12px;
  font-weight: 500;
`;

export default DaekyoEventContainer;

